import type { CacheOwner } from '@/lib/cache/cache-scope'
import type { DisposablePersister } from '@/lib/cache/throttled-persister'
import type { Persister } from '@tanstack/query-persist-client-core'
import type { ReactNode } from 'react'
import { useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import {
  captureRequestScope,
  readIdentityScopeGeneration,
  readRequestScopeGeneration,
  subscribeRequestScope,
} from '@/lib/api/request-scope'
import { browserLoginScope } from '@/lib/auth/browser-login-scope'
import { cacheBusterFor, cacheScopeKey } from '@/lib/cache/cache-scope'
import { createPersister, removeOwnerCaches } from '@/lib/cache/persister'
import {
  getReadCacheMaxAge,
  shouldPersistQuery,
} from '@/lib/cache/query-filter'
import { createLogger } from '@/lib/logger'
import { offlineLedger, restoreOfflineLedger } from '@/lib/offline/ledger'
import {
  isOwnedOfflineEnvelope,
  shouldDehydrateOfflineMutation,
} from '@/lib/offline/offline-queue'
import {
  initializeOfflineQueue,
  resumeOfflineWrites,
} from '@/lib/offline/online-bridge'
import { queryClient } from '@/lib/queryClient'
import { getWorkspaceSessionSnapshot } from '@/lib/session/workspace-session'
import { desktopAccountSession } from '@/lib/utils/native-credential-store'
import { isTauri } from '@/lib/utils/tauri'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { persistQueryClientSubscribe } from '@tanstack/query-persist-client-core'
import {
  hashKey,
  hydrate,
  IsRestoringProvider,
  QueryClientProvider,
} from '@tanstack/react-query'

import { restoreInScope } from '@scrambl/shared/session-scope'

// info is dev-only (production logs warn and up), unlike the bare
// console.info these cache-restore notes used to print for every user.
const logger = createLogger('components/providers/QueryCacheProvider')

export { cacheBusterFor } from '@/lib/cache/cache-scope'

/**
 * Today-dashboard keys and selected desktop workspace lists get a longer window.
 * Today is the landing surface and every widget gates its own skeleton on its
 * own query, so a cold open past the 5-minute window is a full wall of shimmer.
 * Restoring the last-known dashboard paints instantly and revalidates silently.
 * Past 24h the snapshot is dropped outright — a day-old dashboard is a lie.
 */
const TODAY_DASHBOARD_MAX_AGE = 1000 * 60 * 60 * 24 // 24 hours

/**
 * Outer cap for read-cache restore. It has to cover the widest
 * window any key is allowed to use, because it is applied to
 * the persisted blob as a whole; per-key freshness is enforced by the guard
 * below.
 */
const MAX_AGE = TODAY_DASHBOARD_MAX_AGE

/**
 * Per-tab record of which user this tab's in-memory client last belonged to.
 * sessionStorage is deliberate: the leak this guards against is one tab
 * learning about a sign-out/sign-in that happened in another tab of the same
 * profile. Its own React tree never saw an A -> B scope switch, only
 * A -> undefined -> B, and the client still held A's rows.
 */
const CACHE_OWNER_KEY = 'scrambl-cache-owner'
const CACHE_LOGIN_OWNER_KEY = 'scrambl-cache-login-owner-v2'

function readCacheOwner(): CacheOwner | 'legacy' | null {
  try {
    const raw = window.sessionStorage.getItem(CACHE_LOGIN_OWNER_KEY)
    if (raw) {
      const owner: unknown = JSON.parse(raw)
      if (
        owner &&
        typeof owner === 'object' &&
        'userId' in owner &&
        typeof owner.userId === 'string' &&
        'login' in owner &&
        typeof owner.login === 'string'
      )
        return { userId: owner.userId, login: owner.login }
      return 'legacy'
    }
    return window.sessionStorage.getItem(CACHE_OWNER_KEY) ? 'legacy' : null
  } catch {
    // Missing proof must not let a remounted provider retain another login's rows.
    return 'legacy'
  }
}
function writeCacheOwner(owner: CacheOwner | null): void {
  try {
    if (owner) {
      window.sessionStorage.setItem(CACHE_OWNER_KEY, owner.userId)
      window.sessionStorage.setItem(
        CACHE_LOGIN_OWNER_KEY,
        JSON.stringify(owner),
      )
    } else {
      window.sessionStorage.removeItem(CACHE_OWNER_KEY)
      window.sessionStorage.removeItem(CACHE_LOGIN_OWNER_KEY)
    }
  } catch {
    /* The in-memory scope still fences work when storage is unavailable. */
  }
}

function dropInMemoryCache(): void {
  void queryClient.cancelQueries()
  queryClient.clear()
}

/**
 * Wraps a real persister to skip restore if the cached data is too stale.
 * Still persists on every save (so fresh data is always written), but on restore
 * it checks the timestamp and drops what has aged out:
 * - past 5 minutes, Today and selected desktop workspace lists survive
 * - past 24 hours, nothing survives
 */
function createFreshnessGuard(inner: Persister): Persister {
  return {
    persistClient: inner.persistClient,
    removeClient: inner.removeClient,
    restoreClient: async () => {
      const cached = await inner.restoreClient()
      if (!cached) return undefined

      // Paused writes are never subject to the read-cache freshness policy.
      // Those windows exist so nobody is shown yesterday's numbers as if they
      // were today's — a reason that has nothing to do with a change the user
      // made and is still waiting on. Dropping the blob wholesale, as this
      // guard used to, would silently discard queued work; the queue enforces
      // its own, much longer, expiry (OFFLINE_QUEUE_MAX_AGE_MS).
      const mutations = cached.clientState.mutations ?? []

      const age = Date.now() - cached.timestamp
      if (age > TODAY_DASHBOARD_MAX_AGE) {
        logger.info(
          `[QueryCache] Skipping restore — cache is ${Math.round(age / 1000)}s old (max: ${TODAY_DASHBOARD_MAX_AGE / 1000}s)`,
        )
        return mutations.length > 0
          ? { ...cached, clientState: { ...cached.clientState, queries: [] } }
          : undefined
      }

      const now = Date.now()
      const desktop = isTauri()
      const queries = cached.clientState.queries.filter(query => {
        if (!shouldPersistQuery(query.queryKey)) return false
        const maxAge = getReadCacheMaxAge(query.queryKey, desktop)
        // A new snapshot (e.g. a queued write) must not renew old read data.
        // Preserve dataUpdatedAt so mounted stale queries refresh normally.
        return (
          query.state.status === 'success' &&
          query.state.dataUpdatedAt > 0 &&
          now - query.state.dataUpdatedAt <= maxAge
        )
      })

      // Project lists use a runtime access marker to fence live requests.
      // This owner-checked persisted bucket is the one place an ordinary
      // last-known desktop list may be rebound to the new runtime. Detail and
      // permission queries retain their old markers and are never rebound.
      const restoredQueries = queries.map(query => {
        const [root, kind, marker] = query.queryKey
        if (
          !desktop ||
          root !== 'projects' ||
          kind !== 'list' ||
          !marker ||
          typeof marker !== 'object' ||
          !('workspaceSession' in marker)
        )
          return query
        const scope = getWorkspaceSessionSnapshot()
        if (!scope.enabled) return query
        const queryKey = [
          'projects',
          'list',
          { workspaceSession: scope.key },
          ...query.queryKey.slice(3),
        ]
        return { ...query, queryKey, queryHash: hashKey(queryKey) }
      })

      if (queries.length === 0 && mutations.length === 0) return undefined

      logger.info(
        `[QueryCache] Restoring ${queries.length} queries and ${mutations.length} pending writes — cache is ${Math.round(age / 1000)}s old`,
      )
      return {
        ...cached,
        clientState: {
          ...cached.clientState,
          queries: restoredQueries,
        },
      }
    },
  }
}

// Initialised at module scope, not in an effect. `hydrate()` resolves a
// restored mutation's options — including its `mutationFn` — the moment it
// rebuilds it from storage, and restore begins in this provider's first effect.
// Defaults registered later would arrive after that, and every rehydrated write
// would come back inert.
initializeOfflineQueue(queryClient)

/**
 * React observes one stable primitive containing both context and login.
 * Mutable credential captures in render can be memoized by the compiler even
 * when an unrelated generation changes; read them at the external-store boundary.
 */
function readCacheSessionSnapshot(): string {
  const generation = readRequestScopeGeneration()
  const identityGeneration = readIdentityScopeGeneration()
  const native = desktopAccountSession.capture()
  const login = native.identity
    ? JSON.stringify(['desktop', native.identity, native.credentialSessionKey])
    : JSON.stringify(['browser', browserLoginScope.capture().identity])
  return JSON.stringify([generation, identityGeneration, login])
}

export function QueryCacheProvider({ children }: { children: ReactNode }) {
  const userId = useAuth(s => s.user?.id)
  const orgId = useOrganizationStore(s => s.currentOrganization?.id)
  const relationshipId = useOrganizationStore(
    s => s.externalOrgRelationship?.id,
  )
  const [isRestoring, setIsRestoring] = useState(false)
  const sessionSnapshot = useSyncExternalStore(
    subscribeRequestScope,
    readCacheSessionSnapshot,
    readCacheSessionSnapshot,
  )
  const [generation, identityGeneration, login] = JSON.parse(
    sessionSnapshot,
  ) as [number, number, string]
  const externalAccess = useOrganizationStore(
    s => s.currentOrganization?.isExternalAccess ?? false,
  )

  const owner = userId ? { userId, login } : undefined
  const scopeKey =
    owner && orgId
      ? cacheScopeKey(owner, orgId, relationshipId ?? null, externalAccess)
      : undefined
  const previousRef = useRef<{
    scopeKey: string
    owner: CacheOwner
    generation: number
    identityGeneration: number
    persister?: DisposablePersister
  } | null>(null)

  const retiredOwners = useRef<CacheOwner[]>([])
  const retiringWrites = useRef(new Map<string, Set<Promise<void>>>())

  useLayoutEffect(() => {
    const previous = previousRef.current
    const current =
      scopeKey && userId
        ? {
            scopeKey,
            owner: { userId, login },
            generation,
            identityGeneration,
            persister: undefined as DisposablePersister | undefined,
          }
        : null
    previousRef.current = current
    if (
      previous &&
      (previous.scopeKey !== scopeKey || previous.generation !== generation)
    )
      dropInMemoryCache()

    // Storage namespaces are immutable per login. Even a delayed teardown in
    // another tab cannot erase or overwrite this login's cache.
    if (
      previous &&
      (!userId ||
        previous.identityGeneration !== identityGeneration ||
        previous.owner.login !== login ||
        previous.owner.userId !== userId)
    ) {
      if (
        !retiredOwners.current.some(
          old =>
            old.userId === previous.owner.userId &&
            old.login === previous.owner.login,
        )
      )
        retiredOwners.current.push(previous.owner)
      void (async () => {
        await previous.persister?.dispose()
        await previous.persister?.removeClient()
      })().catch(() =>
        console.warn('[QueryCache] Failed to clear the previous login cache'),
      )
    }
    // A different immutable login makes the retired namespace safe to purge.
    // An auth/org bootstrap gap can reuse the same login; delete only the old
    // writer in that case, never a subsequently opened writer's keys.
    if (current) {
      const obsolete = retiredOwners.current.filter(
        old =>
          old.userId !== current.owner.userId ||
          old.login !== current.owner.login,
      )
      retiredOwners.current = retiredOwners.current.filter(
        old =>
          old.userId === current.owner.userId &&
          old.login === current.owner.login,
      )
      for (const old of obsolete)
        void (async () => {
          await Promise.all(
            retiringWrites.current.get(JSON.stringify(old)) ?? [],
          )
          await removeOwnerCaches(old)
        })().catch(() =>
          console.warn(
            '[QueryCache] Failed to retire the previous login cache',
          ),
        )
    }
    if (!current || !userId || !orgId) {
      if (previous) writeCacheOwner(null)
      setIsRestoring(false)
      return
    }
    if (!previous) {
      const previousOwner = readCacheOwner()
      if (
        previousOwner &&
        (previousOwner === 'legacy' ||
          previousOwner.userId !== userId ||
          previousOwner.login !== login)
      )
        dropInMemoryCache()
    }
    writeCacheOwner(current.owner)

    const session = captureRequestScope()
    let cancelled = false
    let unsubscribe: (() => void) | undefined
    const isCurrent = () => {
      if (cancelled || session.signal.aborted) return false
      try {
        session.assertCurrent()
        return true
      } catch {
        return false
      }
    }
    const stop = () => {
      cancelled = true
      unsubscribe?.()
      if (current.persister) {
        const key = JSON.stringify(current.owner)
        const pending =
          retiringWrites.current.get(key) ?? new Set<Promise<void>>()
        const disposal = current.persister.dispose()
        pending.add(disposal)
        retiringWrites.current.set(key, pending)
        void disposal.finally(() => {
          pending.delete(disposal)
          if (!pending.size) retiringWrites.current.delete(key)
        })
      }
    }
    session.signal.addEventListener('abort', stop, { once: true })
    setIsRestoring(true)

    const initializePersistence = async () => {
      await restoreOfflineLedger()
      if (!isCurrent()) return
      const createdPersister = await createPersister(current.scopeKey, () =>
        offlineLedger.list(),
      )
      if (!createdPersister) return
      if (!isCurrent()) {
        await createdPersister.dispose()
        return
      }
      current.persister = createdPersister

      const options = {
        queryClient,
        persister: createFreshnessGuard({
          ...createdPersister,
          persistClient: client =>
            !isCurrent()
              ? Promise.resolve()
              : createdPersister.persistClient(client),
        }),
        maxAge: MAX_AGE,
        buster: cacheBusterFor(userId, login),
        dehydrateOptions: {
          // Only a settled, successful query is worth restoring. A query that
          // errored keeps its previous `data` in React Query's state, and a
          // 401/403 is exactly the case where that data must not outlive the
          // session (queryClient.ts also evicts it); an in-flight query's
          // snapshot is whatever it was before the refetch started.
          shouldDehydrateQuery: (query: {
            state: { status: string; fetchStatus: string; error: unknown }
            queryKey: readonly unknown[]
          }) =>
            query.state.status === 'success' &&
            query.state.error == null &&
            query.state.fetchStatus === 'idle' &&
            shouldPersistQuery(query.queryKey),
          /**
           * Paused writes ride along with the read cache so a change made on a
           * dead connection survives a reload or a closed lid. The predicate is
           * deliberately narrow — paused AND carrying an `['offline', …]`
           * mutation key — which is what keeps financial and destructive
           * writes out of the queue by construction rather than by anyone
           * remembering to exclude them.
           *
           * A persisted mutation is inert without a matching
           * `setMutationDefaults`; those are registered by
           * `initializeOfflineQueue` before restore runs, below.
           */
          shouldDehydrateMutation: shouldDehydrateOfflineMutation,
        },
      }

      const restored = await restoreInScope({
        read: () => options.persister.restoreClient(),
        isCurrent,
        apply: snapshot => {
          if (snapshot && snapshot.buster === options.buster) {
            hydrate(queryClient, {
              ...snapshot.clientState,
              mutations: snapshot.clientState.mutations.filter(
                mutation =>
                  isOwnedOfflineEnvelope(mutation.state.variables) &&
                  mutation.state.variables.ownerId === userId &&
                  mutation.state.variables.organizationId === orgId,
              ),
            })
          }
          unsubscribe = persistQueryClientSubscribe(options)
        },
      })
      if (!restored || !isCurrent()) return
      await resumeOfflineWrites(queryClient)
      // Background-refresh stale restored data. Subscribing first also ensures
      // the current cache is saved even when auth/org resolved after mount.
      if (isCurrent()) await queryClient.invalidateQueries({ stale: true })
    }

    void initializePersistence()
      .catch(() => {
        if (isCurrent())
          console.warn('[QueryCache] Failed to initialize persistence')
      })
      .finally(() => {
        if (isCurrent()) setIsRestoring(false)
      })

    return () => {
      session.signal.removeEventListener('abort', stop)
      stop()
    }
  }, [scopeKey, userId, orgId, login, generation, identityGeneration])

  return (
    <QueryClientProvider client={queryClient}>
      <IsRestoringProvider value={isRestoring}>{children}</IsRestoringProvider>
    </QueryClientProvider>
  )
}
